import axios from 'axios'
import type { RefObject } from 'react'

import { envs } from '@/envs'
import { E_LsKeys } from './ls-keys'
import { getMs, isCordovaApp } from './index'
import { flushCordovaCookies } from './cordova-cookie-manager'
import { isCordovaHttpAvailable, cordovaHttpRequest } from './cordova-http'
import { buildLegacyApiRequestPayload, buildLegacyApiRequestUrl } from './legacy-api-request'


// Таймаут для сетевых запросов (мс)
// Если сеть не отвечает дольше этого времени, запрос прервётся и React Query отдаст кэш
const API_TIMEOUT = getMs('10s')
const UPLOAD_TIMEOUT = getMs('300s')

// Флаг использования нативного HTTP в Cordova
const IS_CORDOVA = isCordovaApp

// NEW API token should live only for the app runtime (in-memory).
// We intentionally DO NOT persist it in localStorage.
let newApiTokenInMemory: string | null = null

// Best-effort cleanup of legacy persisted token from previous versions
try {
  localStorage.removeItem(E_LsKeys.EraToken)
} catch {
  // ignore (storage may be unavailable)
}


type T_MaybeUniRes = {
  code?: string
  message?: string
}

const isVisitorUnauthorized = (res: unknown): res is T_MaybeUniRes & { code: 'visitor-unauthorized' } => {
  return !!res && typeof res === 'object' && (res as any).code === 'visitor-unauthorized'
}

const AUTH_COOKIE_FLUSH_METHODS = new Set([
  'client/visitor/info',
  'client/visitor/authorize_create',
  'client/visitor/authorize_token',
  'client/security/challenge',
  'client/security/confirm',
])

const flushCordovaAuthCookiesSafely = (method: unknown) => {
  if (!AUTH_COOKIE_FLUSH_METHODS.has(String(method))) return
  // Do not block auth flow on cookie persistence.
  // flushCordovaCookies has its own timeout + dedup guard.
  void flushCordovaCookies()
}

const clearCookieByName = (cookieName: string) => {
  document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`
  document.cookie = `${cookieName}=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`
  document.cookie = `${cookieName}=; path=/; domain=.${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`
  // На случай, если кука с .era.vision, а мы на localhost (хотя это cross-domain, но вдруг прокси)
  document.cookie = `${cookieName}=; path=/; domain=.era.vision; expires=Thu, 01 Jan 1970 00:00:01 GMT;`
}

const clearAuthSessionCookies = () => {
  void clearCookieByName
  // ВРЕМЕННО ОТКЛЮЧЕНО:
  // очистка auth-cookie при выходе.
  // clearCookieByName('SID')
  // clearCookieByName('LID')
  // clearCookieByName('LCODE')
}

export const AUTH_LOGOUT_BROADCAST_EVENT = 'era:auth-logout'

export const broadcastLogoutSync = () => {
  const marker = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`

  try {
    localStorage.setItem(E_LsKeys.EraLogoutSync, marker)
  } catch {
    // ignore (storage may be unavailable)
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_BROADCAST_EVENT, {
      detail: { marker },
    }))
  }
}

export const isLogoutGuardActive = () => {
  // ВРЕМЕННО ОТКЛЮЧЕНО:
  // logout guard на основе localStorage.
  return false
}

export const setLogoutGuard = () => {
  // ВРЕМЕННО ОТКЛЮЧЕНО:
  // localStorage.setItem('ERA_LOGOUT_GUARD', '1')
}

export const clearLogoutGuard = () => {
  // ВРЕМЕННО ОТКЛЮЧЕНО:
  // localStorage.removeItem('ERA_LOGOUT_GUARD')
}

export const isDebugAuthEnabled = (): boolean => {
  try {
    // URL flag works even when localStorage is unavailable (e.g. Safari private mode).
    if (typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search)
      if (sp.get('debugAuth') === '1') return true
    }

    // Runtime toggle for debugging in prod/stage:
    // localStorage.setItem('ERA_DEBUG_AUTH', '1')
    // localStorage.removeItem('ERA_DEBUG_AUTH')
    return localStorage.getItem('ERA_DEBUG_AUTH') === '1'
  } catch {
    return false
  }
}

export class VisitorUnauthorizedError extends Error {
  readonly code = 'visitor-unauthorized' as const
  readonly response: unknown

  constructor(response: unknown) {
    super('Пользователь не авторизован (visitor-unauthorized)')
    this.name = 'VisitorUnauthorizedError'
    this.response = response
  }
}

export const isVisitorUnauthorizedError = (e: unknown): e is VisitorUnauthorizedError => {
  return e instanceof VisitorUnauthorizedError
}

// Guard against infinite request loops for `client/visitor/info` when user is not authorized yet.
// In real browsers (especially mobile) some events can cause frequent refetch triggers.
// If backend returns visitor-unauthorized and we don't even have a saved visitor token,
// there is no point in hitting the server repeatedly.
const VISITOR_INFO_UNAUTH_THROTTLE_MS = getMs('30s')
let lastVisitorInfoRes: unknown | null = null
let lastVisitorInfoAt = 0
const LOGOUT_IN_PROGRESS_GUARD_MS = getMs('20s')
let logoutInProgressUntilTs = 0

export const markLogoutInProgress = (ttlMs = LOGOUT_IN_PROGRESS_GUARD_MS) => {
  logoutInProgressUntilTs = Math.max(logoutInProgressUntilTs, Date.now() + ttlMs)
}

export const isLogoutInProgress = () => Date.now() < logoutInProgressUntilTs

export const clearAuthTokens = () => {
  // Protect from race: old in-flight requests should not resurrect refresh token.
  markLogoutInProgress()

  try {
    localStorage.removeItem(E_LsKeys.EraRefreshToken) // token for client/visitor/authorize_token
    // legacy key cleanup (should not be used anymore)
    localStorage.removeItem(E_LsKeys.EraToken)
  } catch {
    // ignore (e.g. storage unavailable)
  } finally {
    newApiTokenInMemory = null
    newApiTokenPromise = null
    // Reset in-memory throttling cache too
    lastVisitorInfoRes = null
    lastVisitorInfoAt = 0
  }
}

export const setNewApiToken = (token: string | null) => {
  newApiTokenInMemory = token
  // legacy key cleanup (defensive)
  try {
    localStorage.removeItem(E_LsKeys.EraToken)
  } catch {
    // ignore
  }
}
// API request (OLD API) - raw (no auto-reauthorize)
const apiRequestRaw = async <Res>(
  body: Record<string, any>,
  options?: {
    skipVisitorInfoUnauthThrottle?: boolean
  }
) => {
  if (body?.method === 'client/visitor/info' && !options?.skipVisitorInfoUnauthThrottle) {
    const hasVisitorToken = !!localStorage.getItem(E_LsKeys.EraRefreshToken)
    const now = Date.now()

    if (isDebugAuthEnabled()) {
      const lastCode = (lastVisitorInfoRes as any)?.code
      // Use warn so it shows even when debug logs are hidden.
      // eslint-disable-next-line no-console
      console.warn('[auth][debug] client/visitor/info called', {
        now,
        hasVisitorToken,
        lastCode,
        sinceLastMs: lastVisitorInfoAt ? (now - lastVisitorInfoAt) : null,
      })
      // eslint-disable-next-line no-console
      console.warn('[auth][debug] client/visitor/info call stack:\n' + (new Error().stack || ''))
    }

    if (!hasVisitorToken && lastVisitorInfoRes && isVisitorUnauthorized(lastVisitorInfoRes)) {
      if (now - lastVisitorInfoAt < VISITOR_INFO_UNAUTH_THROTTLE_MS) {
        if (isDebugAuthEnabled()) {
          // eslint-disable-next-line no-console
          console.warn('[auth][debug] client/visitor/info throttled (return cached visitor-unauthorized)')
        }
        return lastVisitorInfoRes as Res
      }
    }
  }

  const requestUrl = buildLegacyApiRequestUrl(String(body?.method ?? ''))
  const requestPayload = buildLegacyApiRequestPayload(body)

  const res = await axios<Res>(
    requestUrl,
    {
      method: 'POST',
      data: 'data=' + encodeURIComponent(JSON.stringify(requestPayload)),
      withCredentials: true,
      timeout: API_TIMEOUT,
    },
  )

  const data = res?.data

  flushCordovaAuthCookiesSafely(body?.method)

  if (body?.method === 'client/visitor/info') {
    lastVisitorInfoRes = data
    lastVisitorInfoAt = Date.now()
  }

  return data
}

// Raw запрос без auto-reauthorize и без 30-секундного throttle visitor/info.
// Нужен для polling-сценариев авторизации (ожидание подтверждения звонка).
export const apiRequestRawNoVisitorInfoThrottle = async <Res>(
  body: Record<string, any>,
) => {
  return await apiRequestRaw<Res>(body, { skipVisitorInfoUnauthThrottle: true })
}

// ---
// Auth-flow SID keepalive (workaround)
//
// Some environments require a "fresh" SID cookie for `client/visitor/authorize_create`.
// We can't read HttpOnly cookies from JS, but we can trigger the backend to set/refresh SID
// by calling `client/visitor/info` (even if it returns visitor-unauthorized).
// We MUST NOT use `apiRequest` here because it throws VisitorUnauthorizedError by design.
// ---
const AUTH_FLOW_SID_REFRESH_THROTTLE_MS = getMs('15s')
let lastSidRefreshAt = 0
let sidRefreshPromise: Promise<void> | null = null

export const ensureSidCookieForAuthFlow = async () => {
  // Don't spam if clearly offline
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return

  const now = Date.now()
  if (lastSidRefreshAt && now - lastSidRefreshAt < AUTH_FLOW_SID_REFRESH_THROTTLE_MS) return

  if (!sidRefreshPromise) {
    sidRefreshPromise = (async () => {
      try {
        const sidRefreshUrl = buildLegacyApiRequestUrl('client/visitor/info')
        await axios(
          sidRefreshUrl,
          {
            method: 'POST',
            data: 'data=' + encodeURIComponent(JSON.stringify({})),
            withCredentials: true,
            timeout: API_TIMEOUT,
          },
        )
        flushCordovaAuthCookiesSafely('client/visitor/info')
      } catch {
        // ignore - auth flow may still succeed; this is best-effort
      } finally {
        lastSidRefreshAt = Date.now()
      }
    })().finally(() => {
      sidRefreshPromise = null
    })
  }

  await sidRefreshPromise
}

// Deduplicated reauthorize for cookie-session using saved token from client/visitor/info
let visitorReauthorizePromise: Promise<boolean> | null = null

const sleep = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms)
})

const isBrowserOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  // If browser explicitly reports offline — don't waste retries.
  if (navigator.onLine === false) return false
  return true
}

const createOfflineNetworkError = () => {
  const err = new Error('Network Error') as Error & {
    code?: string
    isAxiosError?: boolean
  }
  err.code = 'ERR_NETWORK'
  err.isAxiosError = true
  return err
}

const ensureVisitorAuthorized = async (): Promise<boolean> => {
  if (isLogoutGuardActive()) return false

  const token = localStorage.getItem(E_LsKeys.EraRefreshToken)
  if (!token) return false
  if (!isBrowserOnline()) return false

  if (!visitorReauthorizePromise) {
    visitorReauthorizePromise = (async () => {
      // IMPORTANT:
      // Re-authorization must not loop forever if network/server is flaky.
      // We do a bounded number of attempts with backoff.
      const maxAttempts = 3
      const delaysMs = [ 0, getMs('1500ms'), getMs('4s') ]

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (!isBrowserOnline()) return false

        const delay = delaysMs[attempt - 1] ?? getMs('4s')
        if (delay > 0) await sleep(delay)

        try {
          const res = await apiRequestRaw<T_MaybeUniRes>({
            method: 'client/visitor/authorize_token',
            param: { token },
          })

          if (res?.code === 'ok') return true
        } catch (e) {
          // Swallow and retry (bounded) on transient errors.
          // The caller will proceed without authorization if we fail.
          console.warn('[auth] authorize_token failed', e)
        }
      }

      return false
    })().finally(() => {
      visitorReauthorizePromise = null
    })
  }

  return await visitorReauthorizePromise
}

const maybeStoreVisitorToken = (method: unknown, res: unknown) => {
  if (method !== 'client/visitor/info') return
  if (isLogoutGuardActive()) return
  if (!res || typeof res !== 'object') return
  if (isLogoutInProgress()) return

  const code = (res as any).code
  const token = (res as any)?.data?.token

  if (code === 'ok' && typeof token === 'string' && token) {
    localStorage.setItem(E_LsKeys.EraRefreshToken, token)
  }
}

// API request (OLD API) - auto reauthorize on visitor-unauthorized
const apiRequest = async <Res>(
  body: Record<string, any>,
) => {
  // Ensure we always clear tokens on logout, no matter how/where it is called.
  // IMPORTANT: clear tokens BEFORE calling server logout (per requirement).
  if (body?.method === 'client/visitor/logout') {
    setLogoutGuard()
    broadcastLogoutSync()
    clearAuthTokens()
    try {
      return await apiRequestRaw<Res>(body)
    } finally {
      // При логауте сбрасываем основные auth-cookies
      clearAuthSessionCookies()
    }
  }

  const res = await apiRequestRaw<Res>(body)

  // Сбрасываем logout guard после явно успешного подтверждения авторизации.
  if (
    body?.method === 'client/security/confirm'
    && (res as any)?.code === 'ok'
  ) {
    clearLogoutGuard()
  }

  // ВРЕМЕННО ОТКЛЮЧЕНО:
  // Защитный fallback для «залипшего» logout guard на `client/visitor/info`.
  // if (
  //   body?.method === 'client/visitor/info'
  //   && (res as any)?.code === 'ok'
  //   && isLogoutGuardActive()
  //   && !isLogoutInProgress()
  // ) {
  //   clearLogoutGuard()
  // }

  // Избегаем бесконечных циклов: не пытаемся повторно вызывать authorize_token
  if (body?.method === 'client/visitor/authorize_token') return res

  if (isVisitorUnauthorized(res)) {
    const ok = await ensureVisitorAuthorized()
    if (ok) {
      const retryRes = await apiRequestRaw<Res>(body)
      maybeStoreVisitorToken(body?.method, retryRes)
      return retryRes
    }

    // ВАЖНО:
    // Если не удалось переавторизовать (сетевая ошибка/сервер/токен), НЕ перезаписываем кэшированные успешные данные
    // с payload visitor-unauthorized. Бросаем ошибку, чтобы React Query сохранил предыдущие данные.
    throw new VisitorUnauthorizedError(res)
  }

  maybeStoreVisitorToken(body?.method, res)
  return res
}

const resolveUploadUrl = () => {
  const fallbackUrl = `${envs.API_DOMAIN}/office-data/upload.json`
  // if (IS_CORDOVA) {
  //   return {
  //     primary: fallbackUrl,
  //     fallback: null as string | null,
  //   }
  // }
  //
  // const webOrigin = typeof window !== 'undefined'
  //   ? window.location?.origin?.trim()
  //   : ''
  // if (!webOrigin) {
  //   return {
  //     primary: fallbackUrl,
  //     fallback: null as string | null,
  //   }
  // }
  //
  // const primaryUrl = `${webOrigin}/office-data/upload.json`
  // if (primaryUrl === fallbackUrl) {
  //   return {
  //     primary: primaryUrl,
  //     fallback: null as string | null,
  //   }
  // }
  //
  // return {
  //   primary: primaryUrl,
  //   fallback: fallbackUrl,
  // }
  return {
    primary: fallbackUrl,
    fallback: null as string | null,
  }
}

const uploadWithFetch = async <Res>(url: string, body: FormData) => {
  const abortController = typeof AbortController !== 'undefined'
    ? new AbortController()
    : null
  const abortTimeoutId = abortController
    ? setTimeout(() => abortController.abort(), UPLOAD_TIMEOUT)
    : null

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      body,
      credentials: 'include',
      signal: abortController?.signal,
    })
  } catch (error) {
    if ((error as any)?.name === 'AbortError') {
      const timeoutError = new Error(`Upload request timed out after ${UPLOAD_TIMEOUT}ms`)
      ;(timeoutError as any).code = 'ECONNABORTED'
      throw timeoutError
    }
    throw error
  } finally {
    if (abortTimeoutId !== null) {
      clearTimeout(abortTimeoutId)
    }
  }

  const rawText = await response.text()

  let payload: unknown = null
  if (rawText) {
    try {
      payload = JSON.parse(rawText)
    } catch {
      throw new Error('Invalid JSON response while uploading file')
    }
  }

  if (!response.ok) {
    const err = new Error(`Upload failed with status ${response.status}`)
    ;(err as any).response = {
      status: response.status,
      data: payload,
    }
    throw err
  }

  return payload as Res
}

// API request for file upload (OLD API)
const apiUploadRequest = async <Res>(
  body: FormData,
  options?: {
    legacy?: boolean
  }
) => {
  const uploadUrls = resolveUploadUrl()

  const doRequest = async () => {
    if (options?.legacy) {
      const res = await axios<Res>(
        `${envs.API_DOMAIN}/office-data/upload.json`,
        {
          method: 'POST',
          data: body,
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )

      return res.data
    }

    if (IS_CORDOVA) {
      return await uploadWithFetch<Res>(uploadUrls.primary, body)
    }

    const doAxiosUpload = async (url: string) => {
      const res = await axios<Res>(url, {
        method: 'POST',
        data: body,
        withCredentials: true,
        timeout: UPLOAD_TIMEOUT,
      })

      return res.data
    }

    try {
      return await doAxiosUpload(uploadUrls.primary)
    } catch (error) {
      if (uploadUrls.fallback) {
        return await doAxiosUpload(uploadUrls.fallback)
      }
      throw error
    }
  }

  const res = await doRequest()

  if (isVisitorUnauthorized(res)) {
    const ok = await ensureVisitorAuthorized()
    if (ok) {
      return await doRequest()
    }

    throw new VisitorUnauthorizedError(res)
  }

  return res
}

// API request (NEW API)
let newApiTokenPromise: Promise<string> | null = null
let newApi401RecoveryPromise: Promise<string> | null = null

const ensureNewApiToken = async (): Promise<string> => {
  const existing = newApiTokenInMemory
  if (existing) return existing

  // IMPORTANT:
  // `client/token/generate` requires an authorized visitor session (cookie).
  // On first app open (user not authorized yet) some queries may still call apiRequestNew,
  // and React Query would retry -> causing a request loop.
  //
  // If we don't have a saved visitor token yet, do not even try to hit the server.
  // Throwing VisitorUnauthorizedError ensures React Query keeps cached data and does not retry endlessly.
  const hasVisitorToken = !!localStorage.getItem(E_LsKeys.EraRefreshToken)
  if (!hasVisitorToken) {
    throw new VisitorUnauthorizedError({ code: 'visitor-unauthorized' })
  }

  if (!newApiTokenPromise) {
    newApiTokenPromise = (async () => {
      const res = await apiRequest<{ data?: { token?: string } }>({
        method: 'client/token/generate',
      })

      const token = res?.data?.token
      if (!token) throw new Error('Failed to generate new API token')

      setNewApiToken(token)
      return token
    })().finally(() => {
      newApiTokenPromise = null
    })
  }

  return await newApiTokenPromise
}

const recoverNewApiTokenAfter401 = async (): Promise<string> => {
  if (!newApi401RecoveryPromise) {
    newApi401RecoveryPromise = (async () => {
      // Reset possibly stale token first.
      newApiTokenInMemory = null
      newApiTokenPromise = null

      // Best effort: refresh visitor authorization before generating a new API token.
      // This helps when backend invalidates session and several requests race.
      await ensureVisitorAuthorized()
      return await ensureNewApiToken()
    })().finally(() => {
      newApi401RecoveryPromise = null
    })
  }

  return await newApi401RecoveryPromise
}

const apiRequestNew = async <Res>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  pathname: string,
  body?: any,
  isWithCacheReset?: RefObject<boolean>
) => {
  if (!isBrowserOnline()) {
    throw createOfflineNetworkError()
  }

  const urlPrefix = `${envs.API_DOMAIN}/api/v1`
  const separator = pathname.includes('?') ? '&' : '?'
  // IMPORTANT (CORS):
  // Не добавляем кастомные заголовки Cache-Control/Pragma/Expires.
  // Иначе браузер будет делать preflight (OPTIONS) даже для GET.
  //
  // Если нужно принудительно "обойти кэш", используем cache buster параметр,
  // чтобы GET оставался simple request.
  const headers: Record<string, string> = {}

  if (body && (method === 'POST' || method === 'PUT')) {
    headers['Content-Type'] = 'application/json'
  }

  const isCertificateArchiveDelete = (
    method === 'DELETE'
    && /^\/era\/gift\/certificate\/\d+$/.test(pathname)
  )
  const isCertificateRestoreGet = (
    method === 'GET'
    && /^\/era\/gift\/certificate\/\d+\/restore$/.test(pathname)
  )
  const certificateArchiveDeleteId = isCertificateArchiveDelete
    ? Number(pathname.match(/^\/era\/gift\/certificate\/(\d+)$/)?.[1] || 0)
    : 0

  const options: Record<string, any> = {
    method,
    timeout: API_TIMEOUT,
    headers,
    // Archive DELETE is idempotent for UI flow: 404 means "already archived".
    validateStatus: (status: number) => (
      (status >= 200 && status < 300)
      || (isCertificateArchiveDelete && status === 404)
      || (isCertificateRestoreGet && status === 404)
    ),
  }

  if (body && (method === 'POST' || method === 'PUT')) {
    options.data = JSON.stringify(body)
  }

  const callWithToken = async (token: string) => {
    const cacheBuster = isWithCacheReset?.current ? `&_cb=${Date.now()}` : ''
    const finalUrl = `${urlPrefix}${pathname}${separator}token=${token}${cacheBuster}`
    const res = await axios<Res>(finalUrl, options)
    return res?.data
  }

  const callGetActiveCertificatesWithToken = async (token: string) => {
    const finalUrl = `${urlPrefix}/era/gift/certificate?offset=0&limit=9999&token=${token}`
    const res = await axios<any[]>(finalUrl, {
      method: 'GET',
      timeout: API_TIMEOUT,
    })
    return Array.isArray(res?.data) ? res.data : []
  }

  try {
    const token = await ensureNewApiToken()
    return await callWithToken(token)
  } catch (err: any) {
    const isUnauthorized = Number(err?.response?.status) === 401
    if (!isUnauthorized) throw err

    // Token could be stale on backend side: recover once and retry.
    const freshToken = await recoverNewApiTokenAfter401()

    // For archive DELETE: avoid second DELETE that often ends with 404 noise.
    // If certificate is already absent in active list after token recovery,
    // treat as idempotent success and skip retrying DELETE.
    if (isCertificateArchiveDelete && certificateArchiveDeleteId > 0) {
      try {
        const active = await callGetActiveCertificatesWithToken(freshToken)
        const stillActive = active.some((c) => Number(c?.id) === certificateArchiveDeleteId)
        if (!stillActive) {
          return certificateArchiveDeleteId as Res
        }
      } catch {
        // ignore active-list check failures and fallback to normal retry
      }
    }

    return await callWithToken(freshToken)
  }
}

// API request for Benzuber (OLD API)
const apiRequestBenzuber = async <Res>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  pathname: string,
  body?: any,
) => {
  const url = `${envs.API_DOMAIN}/api${pathname}`

  // IMPORTANT (CORS):
  // Не добавляем Cache-Control/Pragma/Expires, чтобы GET не превращался в preflight (OPTIONS).
  const headers: Record<string, string> = {}

  // В Cordova используем нативный HTTP для обхода CORS
  if (IS_CORDOVA && isCordovaHttpAvailable()) {
    return cordovaHttpRequest<Res>(method, url, body, headers)
  }

  // Стандартный axios для веб-версии
  const res = await axios<Res>(
    url,
    {
      method,
      data: body,
      timeout: API_TIMEOUT,
      headers,
    },
  )

  return res?.data
}


export {
  clearAuthSessionCookies,
  apiRequest,
  apiUploadRequest,
  apiRequestNew,
  apiRequestBenzuber,
}
